import React, { Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { House, DoorOpen } from 'lucide-react';
import { Outlet, useLocation } from 'react-router';

import { Header } from '@layouts/dashboard/components/header';
import { HeaderActions } from '@layouts/dashboard/header-actions';
import { NavMobile } from '@layouts/dashboard/components/nav-mobile';
import { NavContent } from '@layouts/dashboard/components/nav-content';
import { useMenu, useWorkshopMenu } from '@layouts/dashboard/hooks/use-menu';

import { useBoolean } from '@shared/hooks/use-boolean';
import { Spinner } from '@shared/components/ui/spinner';
import { PageHeaderSlotProvider } from '@shared/components/page-header';
import { EnvironmentBanner } from '@shared/components/environment-banner';

import { useUpdateIp } from '@features/auth';
import { useUser } from '@features/user-access';

// Page change: only the content area shows a loader, the sidebar and header stay interactive.
const PageLoader = () => (
  <div className="flex flex-1 items-center justify-center py-20" data-slot="page-loader">
    <Spinner className="size-10" />
  </div>
);

function SuspenseOutlet() {
  const { pathname } = useLocation();
  return (
    <Suspense fallback={<PageLoader />} key={pathname}>
      <Outlet />
    </Suspense>
  );
}

export const AppDashboardLayout = () => {
  const { pathname } = useLocation();
  const { items } = useMenu();
  const { items: workshopItems } = useWorkshopMenu();
  const menu = useBoolean();
  const inWorkshop = pathname === '/workshop' || pathname.startsWith('/workshop/');
  // The dashboard entry lives only in the sidebar: the dashboard's quick access shows the modules alone.
  const navData = inWorkshop
    ? [
        { items: [{ title: "Accueil de l'atelier", path: '/workshop', icon: <House /> }] },
        ...workshopItems,
        { items: [{ title: "Quitter l'atelier", path: '/', icon: <DoorOpen /> }] },
      ]
    : [{ items: [{ title: 'Tableau de bord', path: '/', icon: <House /> }] }, ...items];
  const user = useUser();

  Sentry.setUser({
    fullName: user.userAccess.name,
  });

  useUpdateIp();

  return (
    <PageHeaderSlotProvider>
      <EnvironmentBanner />
      {/* `root__layout` id: used by global.css to stretch the layout to the full height */}
      <div className="flex min-h-full flex-1 flex-col" id="root__layout">
        {/* Fixed sidebar from `lg` (1200px), a sheet below */}
        <aside
          className="fixed inset-y-0 left-0 z-[1201] hidden w-75 flex-col border-r border-border bg-card lg:flex"
          data-slot="sidebar"
        >
          <NavContent data={navData} />
        </aside>
        <NavMobile data={navData} onOpenChange={menu.setValue} open={menu.value} />

        <div className="flex flex-1 flex-col lg:pl-75">
          <Header onOpenMenu={menu.onTrue}>
            <HeaderActions navData={navData} />
          </Header>
          <main className="flex flex-1 flex-col">
            <div className="mx-auto flex w-full max-w-384 flex-1 flex-col px-4 pt-2 pb-16 sm:px-6 lg:px-10">
              <SuspenseOutlet />
            </div>
          </main>
        </div>
      </div>
    </PageHeaderSlotProvider>
  );
};
